/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {openModal} from "helpers/actions";
import BookingAction from "store/booking/BookingAction";
import {useTypedSelector} from "helpers/reducers";
import {RouteComponentProps, withRouter} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useDispatch} from "useDispatch";
import {Package} from "types/entities/Package";
import {packagesCartTimeout, translate} from "utils/utility";
import {filterFitsWithPackageRequirements, isGiftCard, packageHasUpsellItems} from "utils/packageUtils";
import PackageRequirements from "pages/Front/Booking/components/PackageDetail/PackageRequirements";
import BookingFilterMinimal from "pages/Front/Booking/components/BookingFilters/BookingFilterMinimal";
import ButtonSubmit from "pages/Front/Booking/components/ButtonSubmit";
import {ModalSize} from "pages/Front/components/Popup";
import {ThunkAction} from "redux-thunk";
import useNavigation, {RouteName} from "utils/hooks/useNavigation";
import {CURRENCY, IS_UPSELL_ALLOWED} from "helpers/config";
import {DataLayerItem} from "types/entities/specific/DataLayerItem";
import {GAEventName} from "types/entities/specific/GAEventName";
import {useReactGoogleAnalytics} from "utils/useReactGoogleAnalytics";

interface Params {}

interface PackageHandlerProps extends RouteComponentProps<Params> {
    Package: Package;
}

const SelectPackageButton = (props: PackageHandlerProps) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const bookingFilter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const timeslotsAllowed = props.Package.use_timeslots;
    const packagesCart = useTypedSelector(state => state.bookingState.bookingProcess.packageCart);
    const {historyPush} = useNavigation();
    const {sendGenericEvent} = useReactGoogleAnalytics();

    const sendGAData = () => {
        const count = bookingFilter.adults + bookingFilter.children;

        if (!props.Package) {
            return;
        }
        const value = props.Package.price * count;
        const item: DataLayerItem = {
            item_id: props.Package.package_id,
            item_name: translate(props.Package.name),
            quantity: count,
            item_variant: "",
            price: props.Package.price,
            currency: CURRENCY,
        };

        sendGenericEvent(GAEventName.SelectItem, value, [item]);
    };

    const redirectToPackageBooking = (Package: Package) => {
        const upsellItems = packageHasUpsellItems(Package);
        sendGAData();
        if (Package.use_timeslots === 0) {
            // history.replace("/booking/customer/?" + appendQueryParams(filter));
            // return;
            return historyPush(RouteName.Customer, "redirectToPackageBooking - use_timeslots 0");
        }
        if (IS_UPSELL_ALLOWED && upsellItems) {
            // history.replace("/booking/upsell/" + Package.id + "?" + appendQueryParams(filter));
            // return;
            return historyPush(RouteName.UpsellsNew, "redirectToPackageBooking - upsells allowed", Package);
        } else {
            // history.replace("/booking/book/" + Package.id  + "?" + appendQueryParams(filter));
            // return;
            return historyPush(RouteName.Timeslots, "redirectToPackageBooking - no upsells allowed", Package);
        }
    };
    const setSelectedPackageHandler = (Package: Package) => {
        if (isGiftCard(Package)) {
            return historyPush(RouteName.PackageDetail, "selectPackageButton - isGiftcard", Package);
        }

        const disabled = filterFitsWithPackageRequirements(Package, bookingFilter);
        if (props.Package && props.Package.min_drivers_count) {
            if (disabled) {
                dispatch(
                    openModal(
                        <BookingFilterMinimal filterMinimal={true} package={props.Package} proceedToUpsell={true}>
                            <PackageRequirements package={Package} />
                        </BookingFilterMinimal>,
                        {modalSize: ModalSize.Small}
                    )
                );
                return;
            }
        }

        if (disabled) {
            dispatch(
                openModal(
                    <BookingFilterMinimal filterMinimal={true} package={Package} proceedToUpsell={true}>
                        <PackageRequirements package={Package} />
                    </BookingFilterMinimal>,
                    {}
                )
            );
            return;
        }
        dispatch(BookingAction.setPackage(Package));
        //props.setSelectedPackage(Package);

        if (!packagesCart) {
            dispatch<ThunkAction<any, any, any, any>>(BookingAction.initCart()).then(r => redirectToPackageBooking(Package));
            return;
        }

        //upsell package goes before book
        if (timeslotsAllowed) {
            const timedOut = packagesCartTimeout(packagesCart);
            if (timedOut) {
                dispatch<ThunkAction<any, any, any, any>>(BookingAction.initCart()).then(r => redirectToPackageBooking(Package));
            } else {
                redirectToPackageBooking(Package);
            }
        } else {
            sendGAData();
            dispatch<ThunkAction<any, any, any, any>>(
                BookingAction.fetchBookPackage(
                    {
                        timeslot_id: null,
                        package_id: Package.id,
                        adults: bookingFilter.adults,
                        children: bookingFilter.children,
                        upsell: [],
                        hours_count: bookingFilter.hours_count,
                    },
                    packagesCart,
                    "selectPackageButton - without any timeslot given"
                )
            ).then(r => {
                return historyPush(RouteName.Customer, "selectPackageButton - no timeslots allowed");
                // props.history.push("/booking/customer" + "?" + appendQueryParams(bookingFilter));
            });
        }
    };

    return (
        <ButtonSubmit
            icon={"ticket"}
            id={"ButtonSelectPackage-" + props.Package.id}
            label={t(timeslotsAllowed ? "Continue" : isGiftCard(props.Package) ? "Buy gift card" : "Buy ticket")}
            onClick={() => setSelectedPackageHandler(props.Package)}
        />
    );
};

export default withRouter(SelectPackageButton);
